import React, { useMemo, useState, useEffect } from "react";
import { useCreateData } from "hooks/useApiHooks";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useUser } from "contexts/v1/masterData/UserContext";
import { Modal } from "react-bootstrap";

const UserList = ({ formData, setFormData }) => {
  const { users, refetch } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState("");

  // Fungsi untuk menambah atau menghapus user dari daftar yang dipilih
  const handleUserChange = (userId) => {
    setFormData((prev) => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter((id) => id !== userId) // Hapus jika sudah ada
        : [...prev.user_ids, userId], // Tambah jika belum ada
    }));
  };

  const setUsersChecked = (userIds, checked) => {
    setFormData((prev) => {
      const currentIds = new Set(prev.user_ids);
      userIds.forEach((userId) => {
        if (checked) {
          currentIds.add(userId);
        } else {
          currentIds.delete(userId);
        }
      });

      return {
        ...prev,
        user_ids: Array.from(currentIds),
      };
    });
  };

  const handlePhotoClick = (e, url) => {
    e.stopPropagation(); // Mencegah trigger checkbox
    setSelectedPhotoUrl(url);
    setShowPhotoModal(true);
  };

  const filteredUsers = useMemo(() => {
    return users?.data?.filter(user => {
      const keyword = searchTerm.toLowerCase();
      const matchesSearch =
        user.name?.toLowerCase().includes(keyword) ||
        user.email?.toLowerCase().includes(keyword) ||
        user.department?.name?.toLowerCase().includes(keyword) ||
        user.department_name?.toLowerCase().includes(keyword);

      const isSelected = formData.user_ids.includes(user.id);

      if (filterStatus === "checked") return matchesSearch && isSelected;
      if (filterStatus === "unchecked") return matchesSearch && !isSelected;
      return matchesSearch;
    }) || [];
  }, [filterStatus, formData.user_ids, searchTerm, users?.data]);

  const groupedUsers = useMemo(() => {
    return filteredUsers.reduce((groups, user) => {
      const departmentName = user.department?.name || user.department_name || "Tanpa Departemen";
      if (!groups[departmentName]) groups[departmentName] = [];
      groups[departmentName].push(user);
      return groups;
    }, {});
  }, [filteredUsers]);

  const visibleUserIds = filteredUsers.map((user) => user.id);

  return (
    <>
      <div className="card border-0 shadow-sm h-100 d-flex flex-column">
        <div className="card-body border-bottom p-3">
          <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1 fw-bold text-dark">Pilih User Role</h5>
              <div className="text-muted small">
                {formData.user_ids.length} user terpilih dari {users?.data?.length || 0} user
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setUsersChecked(visibleUserIds, true)}>
                Pilih yang tampil
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setUsersChecked(visibleUserIds, false)}>
                Bersihkan
              </button>
              <button type="button" className="btn btn-light btn-sm fw-bold border" onClick={refetch}>
                Refresh
              </button>
            </div>
          </div>

          <div className="row g-2">
            <div className="col-12 col-md">
              <div className="input-icon">
                <span className="input-icon-addon">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon text-muted" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><circle cx="10" cy="10" r="7"></circle><line x1="21" y1="21" x2="15" y2="15"></line></svg>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Cari nama, email, atau departemen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-auto">
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Semua user</option>
                <option value="checked">Sudah dipilih</option>
                <option value="unchecked">Belum dipilih</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-auto bg-light flex-grow-1 p-2 p-md-3" style={{ minHeight: 0 }}>
          {filteredUsers.length > 0 ? (
            Object.entries(groupedUsers).map(([departmentName, departmentUsers]) => {
              const departmentUserIds = departmentUsers.map((user) => user.id);
              const selectedCount = departmentUsers.filter((user) => formData.user_ids.includes(user.id)).length;

              return (
                <div className="card mb-3" key={departmentName}>
                  <div className="card-header py-2 d-flex flex-column flex-sm-row justify-content-between gap-2">
                    <div>
                      <div className="fw-bold">{departmentName}</div>
                      <div className="text-muted small">{selectedCount}/{departmentUsers.length} user dipilih</div>
                    </div>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setUsersChecked(departmentUserIds, true)}>
                        Pilih Dept
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setUsersChecked(departmentUserIds, false)}>
                        Bersihkan
                      </button>
                    </div>
                  </div>
                  <div className="list-group list-group-flush">
                    {departmentUsers.map((user) => {
                      const isSelected = formData.user_ids.includes(user.id);
                      const nameParts = user.name ? user.name.split(" ") : ["U"];
                      const initials = nameParts.length > 1
                        ? nameParts[0][0] + nameParts[1][0]
                        : nameParts[0].slice(0, 2);

                      return (
                        <button
                          type="button"
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 ${isSelected ? "bg-primary-lt" : ""}`}
                          key={user.id}
                          onClick={() => handleUserChange(user.id)}
                        >
                          <input
                            className="form-check-input m-0 flex-shrink-0"
                            type="checkbox"
                            id={`user-${user.id}`}
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ pointerEvents: "none" }}
                          />
                          <div
                            className="flex-shrink-0 d-flex align-items-center justify-content-center overflow-hidden rounded-circle bg-secondary text-white fw-bold"
                            style={{ width: "32px", height: "32px", fontSize: "12px", cursor: user.photo_url ? "pointer" : "default" }}
                            onClick={(e) => user.photo_url ? handlePhotoClick(e, user.photo_url) : e.stopPropagation()}
                          >
                            {user.photo_url ? (
                              <img src={user.photo_url} alt={user.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <span>{initials.toUpperCase()}</span>
                            )}
                          </div>
                          <div className="text-start overflow-hidden flex-grow-1">
                            <div className="fw-bold text-dark text-truncate">{user.name}</div>
                            <div className="text-muted small text-truncate">{user.email || "-"}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted p-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users-x mb-2" width="32" height="32" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 14h14" /><path d="M5 10h14" /><path d="M17 17l4 -4m-4 0l4 4" /></svg>
              <p className="mb-0">Tidak ada user yang cocok dengan pencarian atau filter.</p>
            </div>
          )}
        </div>
                      </div>

      <Modal show={showPhotoModal} onHide={() => setShowPhotoModal(false)} size="md" centered style={{ zIndex: 9999 }}>
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="text-center pt-0 pb-4">
          <img src={selectedPhotoUrl} alt="Detail" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }} />
        </Modal.Body>
      </Modal>
    </>
  );
};

const AssignUser = ({ initialData, onSuccess, onError }) => {
  const API_URL = "/user-role/sync-users";
  const { createData: assignUser, loading: isSubmitting } = useCreateData(
    API_URL,
    "users"
  );

  // State awal dengan user yang sudah ada dalam role
  const [formData, setFormData] = useState({
    role: initialData?.name || "",
    user_ids: [],
  });

  // Auto-select user yang sudah ada dalam role saat komponen dimuat
  useEffect(() => {
    if (initialData?.users) {
      setFormData((prev) => ({
        ...prev,
        user_ids: initialData.users.map((user) => user.id), // Ambil ID user yang sudah ada dalam role
      }));
    }
  }, [initialData]);

  // Hook untuk handle submit
  const { handleSubmit } = useSubmitHandler(assignUser, onSuccess, onError);

  const handleBeforeSubmit = (e) => {
    e.preventDefault();

    // Validasi sebelum submit
    if (!formData.role) {
      alert("Role tidak boleh kosong.");
      return;
    }

    if (formData.user_ids.length === 0) {
      alert("Pilih setidaknya satu user.");
      return;
    }

    handleSubmit(formData, e);
  };

  return (
    <form onSubmit={handleBeforeSubmit} className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {/* Menampilkan role yang sedang diatur */}
      <div className="border rounded bg-light p-3 mb-3">
        <div className="text-muted small">Role yang sedang diatur</div>
        <div className="h3 mb-0">{initialData?.display_name}</div>
        <div className="text-muted small">{initialData?.name}</div>
      </div>

      {/* List user yang bisa dipilih */}
      <div className="action-sheet-content flex-grow-1 overflow-hidden d-flex flex-column">
        <UserList formData={formData} setFormData={setFormData} />
      </div>

      {/* Tombol Submit */}
      <div className="position-sticky bottom-0 bg-white border-top pt-3 mt-3 pb-2">
        <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
          {isSubmitting ? "Processing..." : "Simpan User Role"}
        </button>
      </div>
    </form>
  );
};

export default AssignUser;
